export interface Exterior360Options {
    frameCount: number;
    imagePath: string;
    sensitivity?: number;
    startFrame?: number;
    colorNameElement?: HTMLElement;
}
export class Exterior360 {
    private container: HTMLElement;
    private frames: HTMLImageElement[] = [];
    private currentFrame = 0;
    private isDragging = false;
    private startX = 0;
    private sensitivity: number;
    private frameCount: number;
    private basePath: string;
    private colorNameElement?: HTMLElement;

    constructor(container: HTMLElement, options: Exterior360Options) {
        this.container = container;
        this.sensitivity = options.sensitivity ?? 5;
        this.currentFrame = options.startFrame ?? 0;
        this.frameCount = options.frameCount;
        this.basePath = options.imagePath;
        this.colorNameElement = options.colorNameElement;

        // Ensure container can layer images
        this.container.style.position = "relative";
        this.container.style.overflow = "hidden";

        this.init();
    }

    private async init() {
        const loadedFrames = await this.preloadFrames(this.basePath, this.frameCount);
        if (!loadedFrames.length) return;

        this.frames = loadedFrames;
        this.showFrame(this.currentFrame);
        this.bindEvents();
    }

    private async preloadFrames(basePath: string, frameCount: number) {
        // Case 1: Explicit placeholder like ## or ###
        const hashMatch = basePath.match(/^(.*?)(#+)(\.[a-zA-Z0-9]+)?$/);
        if (hashMatch) {
            const [, prefix, hashes, ext = ""] = hashMatch;
            const numLength = hashes.length;
            return this.loadFramesWithPlaceholder(`${prefix}##${ext}`, frameCount, 1, numLength);
        }

        // Case 2: Numbered suffix, e.g. .../01 or .../001.jpg
        const match = basePath.match(/^(.*?)(\d+)(\.[a-zA-Z0-9]+)?$/);
        if (match) {
            const [, prefix, numStr, ext = ""] = match;
            const numLength = numStr.length;
            const startNum = parseInt(numStr, 10);
            return this.loadFramesWithPlaceholder(`${prefix}##${ext}`, frameCount, startNum, numLength);
        }

        console.error("Exterior360: Could not parse path format:", basePath);
        return [];
    }

    private async loadFramesWithPlaceholder(
        template: string,
        frameCount: number,
        startNum: number,
        numLength: number
    ) {
        const frames: HTMLImageElement[] = [];

        for (let i = 0; i < frameCount; i++) {
            const num = ((startNum - 1 + i) % frameCount) + 1;
            const padded = num.toString().padStart(numLength, "0");
            const url = template.replace("##", padded);

            const img = document.createElement("img");
            img.src = url;
            img.style.display = "none";
            img.style.position = "absolute";
            img.style.top = "0";
            img.style.left = "0";
            img.style.width = "100%";
            img.style.height = "100%";
            img.style.objectFit = "contain";
            img.draggable = false;
            this.container.appendChild(img);
            frames.push(img);

            await img.decode().catch(() => { });
        }

        return frames;
    }

    private bindEvents() {
        const onMouseMove = (e: MouseEvent) => {
            const delta = e.clientX - this.startX;
            if (Math.abs(delta) > this.sensitivity) {
                const direction = delta > 0 ? 1 : -1;
                this.showFrame((this.currentFrame + direction + this.frames.length) % this.frames.length);
                this.startX = e.clientX;
            }
        };

        const onMouseUp = () => {
            this.isDragging = false;
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("mouseup", onMouseUp);
        };

        this.container.addEventListener("mousedown", e => {
            e.preventDefault();
            this.isDragging = true;
            this.startX = e.clientX;
            window.addEventListener("mousemove", onMouseMove);
            window.addEventListener("mouseup", onMouseUp);
        });

        const onTouchMove = (e: TouchEvent) => {
            const delta = e.touches[0].clientX - this.startX;
            if (Math.abs(delta) > this.sensitivity) {
                const direction = delta > 0 ? 1 : -1;
                this.showFrame((this.currentFrame + direction + this.frames.length) % this.frames.length);
                this.startX = e.touches[0].clientX;
            }
        };

        const onTouchEnd = () => {
            this.isDragging = false;
            window.removeEventListener("touchmove", onTouchMove);
            window.removeEventListener("touchend", onTouchEnd);
        };

        this.container.addEventListener("touchstart", e => {
            this.isDragging = true;
            this.startX = e.touches[0].clientX;
            window.addEventListener("touchmove", onTouchMove, { passive: true });
            window.addEventListener("touchend", onTouchEnd);
        });
    }

    private showFrame(index: number) {
        if (!this.frames.length) return;
        this.frames[this.currentFrame].style.display = "none";
        this.frames[index].style.display = "block";
        this.currentFrame = index;
    }

    public async changeBasePath(newBasePath: string, colorName?: string) {
        if (!this.frames.length) return;

        const oldFrames = this.frames;
        const oldFrameIndex = this.currentFrame;

        const newFrames = await this.preloadFrames(newBasePath, this.frameCount);
        if (!newFrames.length) return;

        newFrames.forEach(frame => {
            frame.style.opacity = "0";
            frame.style.transition = "opacity 0.4s ease";
        });

        newFrames[oldFrameIndex].style.display = "block";

        requestAnimationFrame(() => {
            newFrames.forEach(frame => (frame.style.opacity = "1"));
        });

        oldFrames.forEach(frame => {
            frame.style.transition = "opacity 0.4s ease";
            frame.style.opacity = "0";
        });

        setTimeout(() => {
            oldFrames.forEach(f => f.remove());
            this.frames = newFrames;
            this.currentFrame = oldFrameIndex;
        }, 400);

        this.basePath = newBasePath;
        if (colorName && this.colorNameElement) {
            this.colorNameElement.textContent = colorName;
        }
    }

    static initFromDataAttributes(selector = "[data-360]", colorNameSelector = ".color-name") {
        const instances: Record<string, Exterior360> = {};

        document.querySelectorAll<HTMLElement>(selector).forEach(el => {
            const frameCount = parseInt(el.dataset.frames || "0", 10);
            const imagePath = el.dataset.path || "";
            const sensitivity = parseInt(el.dataset.sensitivity || "5", 10);
            const startFrame = parseInt(el.dataset.start || "0", 10);
            const colorNameElement = el.querySelector(colorNameSelector) as HTMLElement;

            if (!frameCount || !imagePath) {
                console.warn("Exterior360: Missing required data attributes", el);
                return;
            }

            const instance = new Exterior360(el, {
                frameCount,
                imagePath,
                sensitivity,
                startFrame,
                colorNameElement
            });

            if (el.id) {
                instances[el.id] = instance;
            }
        });

        document.querySelectorAll<HTMLButtonElement>(".selected-object").forEach(btn => {

            btn.addEventListener("click", () => {
                const targetId = btn.dataset.target;
                const path = btn.dataset.path;
                const color = btn.dataset.color;
                if (!targetId || !instances[targetId] || !path) return;
                instances[targetId].changeBasePath(path, color);

                document.querySelector<HTMLElement>(`[data-color-target="${targetId}"]`).innerHTML = color;

                // Add active class to clicked button
                document.querySelectorAll<HTMLButtonElement>(`.selected-object[data-target="${targetId}"]`).forEach(btn => btn.classList.remove("active"));
                btn.classList.add("active");
            });
        });


        return instances;
    }
}
